import React, { useContext, useState } from 'react';
import { TextInput } from '../Shared/Inputs/TextInput';
import { useNavigate } from 'react-router-dom';
import { RecruiterRegisterModel, RecruiterRegisterValidation } from '../../models/RecruiterRegisterModel';
import { FileInput } from '../Shared/Inputs/FileInput';
import { CheckboxInput } from '../Shared/Inputs/CheckboxInput';
import { Link } from 'react-router-dom';
import { Button } from '../Shared/Buttons/Button';
import useErrorPage from '../../hooks/useErrorPage';
import { 
    validateEmail, 
    validateImageFile, 
    validateName, 
    validatePasswordRules, 
    validatePhoneNumber 
} from '../../helpers/Common';
import { PasswordValidation } from '../Shared/PasswordValidationPanel';
import { AuthContext } from '../../providers/AuthContextProvider';
import { postRecruiterRegister } from '../../services/RecruiterService';
import { ResponseModel } from '../../models/ResponseModel';
import { SubmitFormText } from '../../helpers/LayoutTexts';
import { RegisterFormLoader } from '../Loaders/RegisterFormLoader';

interface Props {
    styles: any;
    title: string;
    callbackUrl: string | null;
    message: string | null;
    openAlert(status: string, message: string): void;
    changeStage(stage: string): void;
}

const initialRegisterModel: RecruiterRegisterModel = {
    Organization: {
        OrganizationName: "",
        Telephone: ""
    },
    OrganizationLogo: "",
    Forename: "",
    Surname: "",        
    Email: "",
    Password: "",
    ConfirmPassword: "",          
    PrivacyPolicy: true
}

const initialRegisterValidation: RecruiterRegisterValidation = {
    OrganizationName: {},
    OrganizationLogo: {},
    Forename: {},
    Surname: {},
    Email: {},
    Telephone: {},
    Password: {},
    ConfirmPassword: {},
    PrivacyPolicy: {valid: true}
}

export const Register: React.FC<Props> = (props) => {
    const navigate = useNavigate();    
    const [registerModel, setRegisterModel] = useState<RecruiterRegisterModel>(initialRegisterModel);
    const [validation, setValidation] = useState<RecruiterRegisterValidation>(initialRegisterValidation);
    const [passwordFocused, setPasswordFocused] = useState<boolean>(false);
    const [organizationLogo, setOrganizationLogo] = useState<File>();
    const [toErrorPage] = useErrorPage();
    const [loading, setLoading] = useState(false);
    const styles = props.styles;
    const authContext = useContext(AuthContext);

    const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value; 
        if(name === "OrganizationName" || name === "Telephone"){
            setRegisterModel((values: any) => ({
                ...values,
                Organization: { ...values.Organization, [name]: value}
            }));
        }
        else{
            setRegisterModel((values: any) => ({ ...values, [name]: value }));
        }
        if(validation[name] && validation[name].invalid) {
            validateInput(name, value);
        }
        if (name === "Password" && registerModel.ConfirmPassword !== "") {
            if (value !== registerModel.ConfirmPassword) {
                setValidation((v: any) => ({
                    ...v,
                    ConfirmPassword: { valid: false, invalid: true, invalidMessage: "Password and confirmation do not match" }
                }));
            } else {
                setValidation((v: any) => ({
                    ...v,
                    ConfirmPassword: { valid: true, invalid: false }
                }));
            }
        }
    } 

    const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.checked;
        setRegisterModel((values: any) => ({ ...values, [name]: value }));
        if(name === "PrivacyPolicy" && !value) { 
            setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "You should agree to Privacy Policy & Terms/Conditions"}})); 
        }
    }

    const handleFileChange = (selectedFiles: FileList) => {
        if(selectedFiles.length > 0) {
            setOrganizationLogo(selectedFiles[0]);
            setRegisterModel((values: any) => ({ ...values, ["OrganizationLogo"]: selectedFiles[0].name }));
            let organizationLogoValidation = validateImageFile(selectedFiles[0]);
            setValidation((values: any) => ({...values, ["OrganizationLogo"]: organizationLogoValidation}));
        }
    }

    const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
        const name = event.target.name;
        const value = event.target.value;
        validateInput(name, value); 
    }
    
    const validateInput = (name: string, value: string) => {
        if(name === "OrganizationName") {
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Organization Name is required"}}));
            }
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "Forename" || name === "Surname") {
            setValidation((values: any) => ({...values, [name]: validateName(name, value, 200)}));            
        }
        if(name === "Email") {            
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Email is required"}}));
            } 
            else if (!validateEmail(value)) {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Invalid email address!"}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "Password") {
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Password is required"}}));
            } 
            else if(!validatePasswordRules(value).isValid) {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Password does not meet the requirements"}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }
        if(name === "ConfirmPassword") {
            const password = registerModel.Password;      
            if(value === "") {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Password confirmation is required"}}));
            } 
            else if (password !== value) {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Password and confirmation do not match"}}));
            } 
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        } 
        if(name === "Telephone" && value !== "") {
            if(!validatePhoneNumber(value)) {
                setValidation((values: any) => ({...values, [name]: {valid: false, invalid: true, invalidMessage: "Invalid Mobile number!"}}));
            }
            else {
                setValidation((values: any) => ({...values, [name]: {valid: true, invalid: false}}));
            }
        }  
    }

    const validateForm = (): number => {
        let formValidation: RecruiterRegisterValidation = {
            OrganizationName: {valid: true},
            OrganizationLogo: {valid: true},
            Telephone: {valid: true},
            Forename: {valid: true},
            Surname: {valid: true},
            Email: {valid: true},
            Password: {valid: true},
            ConfirmPassword: {valid: true},
            PrivacyPolicy: {valid: true}            
        }
        if(registerModel.Organization.OrganizationName === "") {
            formValidation.OrganizationName  = {valid: false, invalid: true, invalidMessage: "Organization Name is required"};
        }
        if(organizationLogo){
            formValidation.OrganizationLogo = validateImageFile(organizationLogo);
        } 
        formValidation.Forename = validateName("Forename", registerModel.Forename, 200);                
        formValidation.Surname = validateName("Surname", registerModel.Surname, 200);
        if(registerModel.Email === "") {
            formValidation.Email = {valid: false, invalid: true, invalidMessage: "Email is required"};
        } 
        else if(!validateEmail(registerModel.Email)) {
            formValidation.Email = {valid: false, invalid: true, invalidMessage: "Invalid email address!"};
        }
        if(registerModel.Password === "") {
            formValidation.Password = {valid: false, invalid: true, invalidMessage: "Password is required"};
        }
        else if(!validatePasswordRules(registerModel.Password).isValid) {
            formValidation.Password = {valid: false, invalid: true, invalidMessage: "Password does not meet the requirements"};
        }
        if(registerModel.ConfirmPassword === "") {
            formValidation.ConfirmPassword = {valid: false, invalid: true, invalidMessage: "Password confirmation is required"};
        } 
        else if(registerModel.Password !== registerModel.ConfirmPassword) {
            formValidation.ConfirmPassword = {valid: false, invalid: true, invalidMessage: "Password and confirmation do not match"};
        } 
        if(registerModel.Organization.Telephone !== "" && !validatePhoneNumber(registerModel.Organization.Telephone)) {
            formValidation.Organization  = {valid: false, invalid: true, invalidMessage: "Invalid Mobile number!"};
        }
        if(!registerModel.PrivacyPolicy) {
            formValidation.PrivacyPolicy = {valid: false, invalid: true, invalidMessage: "You should agree to Privacy Policy & Terms/Conditions"};
        }
        setValidation(formValidation);        
        const validInputCount = Object.keys(formValidation).filter(key => formValidation[key].valid).length;
        return validInputCount;
    }

    async function submitRegistration(event: React.FormEvent<HTMLFormElement>) {
        event.preventDefault();
        let validInputCount = Object.keys(validation).filter(key => validation[key].valid).length;
        if(validInputCount < 9){
            validInputCount = validateForm();
        }
        if(validInputCount === 9) {  
            setLoading(true)       
            const formData = new FormData();
            formData.append("recruiterModelString", JSON.stringify(registerModel));
            formData.append("organizationLogo", organizationLogo!);
            await postRecruiterRegister(formData)
            .then((registerResponse: ResponseModel) => {
                setLoading(false);
                if(registerResponse.status) {
                    authContext.setLoggedRecruiter(registerResponse.data, registerResponse.token); 
                    if(props.callbackUrl) {
                        navigate(props.callbackUrl);
                    } 
                    else {
                        navigate('/recruiter/vacancy');
                    }
                }
                else {
                    props.openAlert("error", registerResponse.message);
                }
            })
            .catch((error) => {            
                toErrorPage(error);
            });
        }
        else {                        
            props.openAlert("error", SubmitFormText.error);        
        }
    }

    return (
        <>
            {loading ? <RegisterFormLoader displaySocialLogin={false}/> :
            <form style={{...styles.registerForm, ...(props.message && styles.registerForm.messageEnabled)}} onSubmit={submitRegistration}>
                <div style={styles.accordionButtonsContainer}>
                    <div
                        style={{ ...styles.accordionButton, ...styles.accordionButton.left }}
                        onClick={() => props.changeStage("login")} >
                        Login
                    </div>
                    <div
                        style={{ ...styles.accordionButton, ...styles.accordionButton.right, ...styles.accordionButton.selected }}
                        onClick={() => props.changeStage("register")} >
                        Register
                    </div>
                </div>
                {props.message && <div style={styles.callbackMessage}>{props.message}</div>}
                <p style={{...styles.boldTitle, ...styles.boldTitle.register}}>{props.title}</p>
                <div style={styles.row}>
                    <TextInput
                        label={"Organization Name *"}
                        inputType={'text'}
                        name={"OrganizationName"}
                        value={registerModel.Organization.OrganizationName}
                        handleChange={handleInputChange}
                        handleBlur={handleInputBlur}
                        validation={validation.OrganizationName}                    
                    />
                    <FileInput
                        label={'Choose Logo'}             
                        id={"Organization-Logo"}
                        name={"OrganizationLogo"}                
                        selectedFileName={registerModel.OrganizationLogo}
                        styles={styles.organizationLogoUpload}
                        handleChange={(event) => handleFileChange(event!.currentTarget.files!)}
                        validation={validation.OrganizationLogo}                     
                    />
                </div>
                <div style={styles.fileWarning}><span style={styles.fileWarning.bold}>Supported Files</span> : png, jpg, jpeg | <span style={styles.fileWarning.bold}>Max file size</span> : 2MB</div>
                <div style={styles.row}>
                    <TextInput
                        label={"First Name *"}
                        styles={styles.registerRightInput}
                        inputType={'text'}
                        name={"Forename"}
                        value={registerModel.Forename}
                        handleChange={handleInputChange}
                        handleBlur={handleInputBlur}
                        validation={validation.Forename}                    
                    />
                    <TextInput
                        label={'Last Name *'}
                        styles={styles.registerLeftInput}
                        inputType={'text'}                    
                        name={"Surname"}
                        value={registerModel.Surname}
                        handleChange={handleInputChange}
                        handleBlur={handleInputBlur}
                        validation={validation.Surname}
                    />
                </div>
                <div style={styles.row}>
                    <TextInput
                        label={"Email *"}
                        styles={styles.registerRightInput}
                        inputType={'text'}
                        name={"Email"}
                        value={registerModel.Email}
                        handleChange={handleInputChange}
                        handleBlur={handleInputBlur}
                        validation={validation.Email}                    
                    />
                    <TextInput
                        label={"Phone"}
                        styles={styles.registerLeftInput}
                        inputType={'text'}
                        name={"Telephone"}
                        value={registerModel.Organization.Telephone}
                        handleChange={handleInputChange}
                        handleBlur={handleInputBlur}
                        validation={validation.Telephone}                    
                    />
                </div>
                <div style={styles.row}>
                    <div
                        style={styles.row.password}
                        onFocus={() => setPasswordFocused(true)}
                        onBlur={(e) => {
                            if (!e.currentTarget.contains(e.relatedTarget as Node)) 
                                setPasswordFocused(false);
                        }}
                    >
                        <TextInput
                            label={'Password *'}
                            styles={styles.registerRightInput}
                            inputType={'password'}
                            name={"Password"}
                            value={registerModel.Password}
                            handleChange={handleInputChange}
                            handleBlur={handleInputBlur}
                            validation={validation.Password}
                        />
                        <PasswordValidation
                            password={registerModel.Password}
                            touched={validation.Password?.invalid}
                            showStrength
                            focused={passwordFocused}
                        />
                    </div>
                    <TextInput
                        label={'Confirm Password *'}
                        styles={styles.registerLeftInput}
                        inputType={'password'}
                        name={"ConfirmPassword"}                    
                        value={registerModel.ConfirmPassword}
                        handleChange={handleInputChange}
                        handleBlur={handleInputBlur}
                        validation={validation.ConfirmPassword}
                    />
                </div>
                <div style={styles.policyWrapper}>
                    <div style={{...styles.row,...(!registerModel.PrivacyPolicy && styles.policyLabelError)}}>
                        <CheckboxInput name="PrivacyPolicy" value={registerModel.PrivacyPolicy} handleChange={handleCheckboxChange}>
                            <span style={styles.policyLabel}>I agree to <Link style={{...styles.sharpText.black,...(!registerModel.PrivacyPolicy && styles.policyLabelError)}} to={'/privacyPolicy'}>Privacy Policy</Link> {"&"} <Link style={{...styles.sharpText.black,...(!registerModel.PrivacyPolicy && styles.policyLabelError)}} to={'/termsconditions'}>Terms/Conditions.</Link></span>
                        </CheckboxInput>
                    </div>        
                </div>
                <div style={styles.row}>
                    <div style={styles.registerWrapper}>
                        <Button style={styles.submitButton} type="submit" disabled={loading}>Register</Button>
                    </div>
                    <div style={styles.loginNowSection}>
                        <p style={styles.loginNowWrapper}>Already have an account ? <span style={styles.sharpText.bold} onClick={() => props.changeStage("login")}>Login Now</span></p>
                    </div>
                </div>
            </form>}
        </>
    )
}